import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../../services/config/api';
import AuthLayout, { trackPointer, Field, AlertCircleIcon, CheckCircleIcon, ArrowRightIcon, LockIcon, EyeButton } from '../../Auth/AuthLayout';

function ShieldIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z"/>
            <path d="M9 12l2 2 4-4"/>
        </svg>
    );
}

const AUDIT_MODULES = [
    { title: 'Internal Audit',    code: 'AUDIT',  tag: 'Plan & track audits',      icon: 'compliance' },
    { title: 'Continuous Compliance', code: 'GRC', tag: 'Weeks, not months',       icon: 'compliance' },
    { title: 'Risk Register',     code: 'RISK',   tag: 'Know your exposure',        icon: 'tpcrm'      },
    { title: 'AI Governance',     code: 'AI',     tag: 'Adopt AI safely',           icon: 'nova'       },
];

function InternalAuditSignup() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const inviteToken = searchParams.get('token');

    const [form, setForm] = useState({ password: '', confirm_password: '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!inviteToken) {
            setError('Invalid or missing invite link. Please contact your administrator.');
        }
    }, [inviteToken]);

    const calculateStrength = (password) => {
        let strength = 0;
        if (password.length >= 10) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[!@#$%^&*()]/.test(password)) strength++;
        return Math.min(strength, 4);
    };
    const strength = calculateStrength(form.password);

    const handleRegister = async (e) => {
        if (e) e.preventDefault();
        if (!form.password) { setError('Password is required.'); return; }
        if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
        if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return; }
        setLoading(true); setError('');
        try {
            const axios = (await import('axios')).default;
            const res = await axios.post(
                API.AUDIT_PORTAL_REGISTER,
                { password: form.password, token: inviteToken },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Basic YmFzZWFkbWluOmRlZmF1bHRANDU2',
                        'accept': 'application/json',
                    },
                }
            );
            const data = res?.data?.response_data?.data;
            if (data?.email || res?.data?.response_data?.message) {
                setDone(true);
                setMessage(
                    `Account activated${data?.email ? ` for ${data.email}` : ''}. You can now log in.`
                );
            } else {
                setError(res?.data?.response_data?.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            const errMsg =
                err?.response?.data?.response_data?.message ||
                err?.response?.data?.error?.message ||
                err?.response?.data?.detail || '';
            setError(errMsg || 'Registration failed. The invite link may be expired or already used.');
        }
        setLoading(false);
    };

    return (
        <AuthLayout>
            <AuthLayout.Stage
                eyebrow="Internal Audit Portal"
                headline="Audit with"
                accent="confidence."
                modules={AUDIT_MODULES}
            />
            <AuthLayout.Panel>
                <div className="lg-wrap">
                    <header className="lg-form-header">
                        <p className="lg-eyebrow"><ShieldIcon/> Activate account</p>
                        <h1 className="lg-title" id="lg-ia-title">
                            {done ? "You're all set!" : 'Set Your Password'}
                        </h1>
                        <p className="lg-lead">
                            {done
                                ? 'Your account is ready. Log in to access the Audit Portal.'
                                : "You've been invited to the Internal Audit Portal. Create a password to get started."}
                        </p>
                    </header>

                    {error && (
                        <div className="lg-alert lg-alert--error" role="alert">
                            <AlertCircleIcon/> <span>{error}</span>
                        </div>
                    )}
                    {message && (
                        <div className="lg-alert lg-alert--success" role="status">
                            <CheckCircleIcon/> <span>{message}</span>
                        </div>
                    )}

                    {done ? (
                        <button
                            type="button"
                            className="lg-submit lg-spot"
                            onPointerMove={trackPointer}
                            onClick={() => navigate('/audit-portal/login')}
                        >
                            <span className="lg-submit-label">Go to Login <ArrowRightIcon/></span>
                        </button>
                    ) : (
                        <form className="lg-form" noValidate onSubmit={handleRegister}>
                            <Field
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                label="New password"
                                icon={<LockIcon/>}
                                value={form.password}
                                onChange={e => { setForm({ ...form, password: e.target.value }); setError(''); }}
                                autoComplete="new-password"
                                trailing={<EyeButton visible={showPassword} onToggle={() => setShowPassword(s => !s)}/>}
                            />

                            <div className="lg-strength-wrap" data-s={strength}>
                                <div className="lg-strength" data-s={strength}>
                                    {Array.from({ length: 4 }).map((_, idx) => (
                                        <div key={idx} className={`lg-str-bar${idx < strength ? ' lg-str-bar--on' : ''}`}/>
                                    ))}
                                </div>
                                <span className="lg-strength-label">
                                    {['', 'Weak', 'Fair', 'Good', 'Strong'][strength] || ''}
                                </span>
                            </div>

                            <Field
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                label="Confirm password"
                                icon={<LockIcon/>}
                                value={form.confirm_password}
                                onChange={e => { setForm({ ...form, confirm_password: e.target.value }); setError(''); }}
                                error={form.confirm_password && form.confirm_password !== form.password ? 'Passwords do not match.' : ''}
                                autoComplete="new-password"
                                trailing={<EyeButton visible={showConfirmPassword} onToggle={() => setShowConfirmPassword(s => !s)}/>}
                            />

                            <button
                                type="submit"
                                className="lg-submit lg-spot"
                                disabled={loading || !inviteToken}
                                aria-busy={loading}
                                onPointerMove={trackPointer}
                            >
                                {loading ? (
                                    <span className="lg-submit-label"><span className="lg-spin" aria-hidden="true"/> Activating account...</span>
                                ) : (
                                    <span className="lg-submit-label">Set Password &amp; Continue <ArrowRightIcon/></span>
                                )}
                                {loading && <i className="lg-scan" aria-hidden="true"/>}
                            </button>
                        </form>
                    )}

                    {!done && (
                        <p className="lg-signup">
                            Already have an account?{' '}
                            <button type="button" className="lg-link" onClick={() => navigate('/audit-portal/login')}>Log in</button>
                        </p>
                    )}
                </div>
            </AuthLayout.Panel>
        </AuthLayout>
    );
}

export default InternalAuditSignup;
